import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useResultLetterNotPrintedCount, useResultLetterNotPrintedData, useDeleteExam } from '../../../hooks/useWidgetData';
import { toast } from '@rickylandino/react-messages';
import { CheckCircle, Trash2 } from 'lucide-react';
import { Table, Modal } from 'antd';
import Globals from '../../../config/globals';
import PatientExamPane from '../../Exams/PatientExamPane';
import { calculateAge } from '../../Helpers/PatientHelper';
import { getDeletionData, getPatientExamResults } from '../../../common/services/ExamService';
import { renderResultLetter } from '../../../common/services/next-gen/WidgetDataService';

/**
 * ResultLettersNotPrintedWidget component
 * Shows all result letters that have not been printed
 * @param {Object} props
 * @param {boolean} [props.isCollapsed=false] - Whether the widget is collapsed
 * @param {boolean} [props.isExpanded=false] - Whether the widget is in fullscreen mode
 * @param {Function} [props.setIsLoading] - Callback to set loading state for parent
 * @param {Function} [props.setSharedState] - Callback to update shared state (including refetch)
 */
export default function ResultLettersNotPrintedWidget({ isCollapsed = false, isExpanded = false, setIsLoading, setSharedState, queryEnabled = true }) {
    // Get query client for cache invalidation
    const queryClient = useQueryClient();
    
    // Delete exam mutation
    const deleteExamMutation = useDeleteExam();

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    
    // Print loading state
    const [printingRowId, setPrintingRowId] = useState(null);
    
    // Exam pane state
    const [showAddExamPane, setShowAddExamPane] = useState(false);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [selectedPatientExam, setSelectedPatientExam] = useState(null);
    const [examResults, setExamResults] = useState(null);
    const [sharedFollowUpIDs, setSharedFollowUpIDs] = useState(null);
    const [facility, setFacility] = useState(null);
    
    // Deletion modal state
    const [showDeletionModal, setShowDeletionModal] = useState(false);
    const [deletionReasons, setDeletionReasons] = useState([]);
    const [deletionReason, setDeletionReason] = useState('');
    const [examToDelete, setExamToDelete] = useState(null);
    const [shouldDeleteGroupedExam, setShouldDeleteGroupedExam] = useState(false);
    const [groupedExamType, setGroupedExamType] = useState('');

    // Determine when to load full data: either expanded in card OR in fullscreen mode
    const shouldLoadFullData = !isCollapsed || isExpanded;

    // Fetch count when collapsed AND not expanded
    const { data: countData, isLoading: isLoadingCount } = useResultLetterNotPrintedCount((isCollapsed && !isExpanded) && queryEnabled);

    // Fetch full data when expanded in card or fullscreen
    const { 
        data, 
        isLoading, 
        isFetching,
        isError, 
        error,
        refetch
    } = useResultLetterNotPrintedData(currentPage, pageSize, shouldLoadFullData);

    const results = data?.data || [];
    const totalCount = data?.totalCount || 0;
    const count = shouldLoadFullData ? totalCount : (countData?.count || 0);

    // Notify parent of loading state for fullscreen overlay
    useEffect(() => {
        if (setIsLoading && isExpanded) {
            setIsLoading(isLoading || isFetching);
        }
    }, [isLoading, isFetching, isExpanded, setIsLoading]);

    // Show error toast when full data fails to load
    useEffect(() => {
        if (shouldLoadFullData && isError) {
            console.error('Failed to load result letters not printed:', error);
            toast.error(`Failed to load result letters not printed: ${error?.message || 'Unknown error'}`);
        }
    }, [shouldLoadFullData, isError, error]);

    // Set refetch function in shared state when it becomes available
    useEffect(() => {
        if (setSharedState && refetch) {
            setSharedState(prev => ({ ...prev, refetch }));
        }
    }, [setSharedState, refetch]);

    /**
     * Format MRN with spaces every 10 characters - memoized
     */
    const formatMRN = useCallback((mrn) => {
        if (!mrn) return '';
        return mrn.replace(/(.{10})/g, '$1 ').trim();
    }, []);

    /**
     * Format date to short format - memoized
     */
    const formatDate = useCallback((dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            month: 'numeric', 
            day: 'numeric', 
            year: 'numeric' 
        });
    }, []);

    /**
     * Sorted results - most recent result dates first
     */
    const sortedResults = useMemo(() => {
        if (!results || results.length === 0) return [];
        
        return [...results].sort((a, b) => {
            const aDate = new Date(a.resultDate || a.examDateTime).getTime();
            const bDate = new Date(b.resultDate || b.examDateTime).getTime();
            return bDate - aDate; // Most recent first
        });
    }, [results]);

    //const handlePrint = async () => {
    //    if (!patientId) return;

    //    setIsPrintLoading(true);
    //    try {
    //        const blob = await reportService.getHxSheetReport({ patientId });
    //        const url = globalThis.URL.createObjectURL(blob);
    //        globalThis.open(url, '_blank');
    //        // Clean up the URL after a delay to allow the browser to open it
    //        setTimeout(() => globalThis.URL.revokeObjectURL(url), 100);
    //    } catch (error) {
    //        console.error('Error generating HxSheet report:', error);
    //        // Optionally show an error toast here
    //    } finally {
    //        setIsPrintLoading(false);
    //    }
    //};
    /**
     * Handle printing a result letter
     */
    const handlePrint = useCallback(async (row, rowId) => {
        setPrintingRowId(rowId);
        try {
            // Prepare request payload
            const postdata = {
                patientResultId: row.patientResultId,
                ianaTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone
            };

            // Fetch and render result letter
            const blob = await renderResultLetter(postdata);
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank');
            // Clean up the URL after a delay to allow the browser to open it
            setTimeout(() => window.URL.revokeObjectURL(url), 100);

            // Invalidate queries since printing removes the record from this list
            queryClient.invalidateQueries({ queryKey: ['widgetData', 'result-letters-not-printed', currentPage, pageSize] });
            queryClient.invalidateQueries({ queryKey: ['widgetCount', 'result-letters-not-printed'] });

        } catch (error) {
            console.error('Error printing result letter:', error);
            toast.error(`Failed to print result letter: ${error?.message || 'Unknown error'}`);
        } finally {
            setPrintingRowId(null);
        }
    }, [queryClient, currentPage, pageSize]);

    /**
     * Handle deleting an exam - loads deletion reasons and shows modal
     */
    const handleDeleteExam = useCallback(async (row) => {
        try {
            // Load deletion reasons
            const reasons = await getDeletionData(row.groupedExam);
            
            setDeletionReasons(reasons?.reasonForDeletion || []);
            setGroupedExamType(reasons?.groupedExamType || "");
            setExamToDelete(row);
            setShowDeletionModal(true);
            
        } catch (error) {
            console.error('Error loading deletion reasons:', error);
            toast.error('Failed to load deletion reasons');
        }
    }, []);

    /**
     * Close deletion modal and reset state
     */
    const closeDeletionModal = useCallback(() => {
        setShowDeletionModal(false);
        setDeletionReason('');
        setExamToDelete(null);
        setShouldDeleteGroupedExam(false);
    }, []);

    /**
     * Handle the actual deletion after reason is selected
     */
    const handleConfirmDelete = useCallback(async () => {
        if (!deletionReason) {
            toast.warning('Please select a reason for deletion');
            return;
        }

        try {
            // Validate required fields
            if (!examToDelete?.patientExamId) {
                toast.error('Missing exam ID');
                return;
            }

            // Call the delete mutation
            await deleteExamMutation.mutateAsync({
                examId: examToDelete.patientExamId,
                reasonForDeletion: deletionReason,
                deleteBothGroupedExams: shouldDeleteGroupedExam
            });

            // Close modal
            closeDeletionModal();
            
            // Show success message
            toast.success('Exam deleted successfully');

        } catch (error) {
            console.error('Error deleting exam:', error);
            toast.error(`Failed to delete exam: ${error?.response?.data?.message || error?.message || 'Unknown error'}`);
        }
    }, [deletionReason, examToDelete, shouldDeleteGroupedExam, deleteExamMutation, closeDeletionModal]);

    /**
     * Update exam info after changes
     */
    const updateExamInfo = useCallback(() => {
        toast.destroy('loading');
        toast.destroy("updating");

        setShowAddExamPane(false);

        // Invalidate queries to trigger automatic refetch
        queryClient.invalidateQueries({ queryKey: ['widgetData', 'result-letters-not-printed', currentPage, pageSize] });
        queryClient.invalidateQueries({ queryKey: ['widgetCount', 'result-letters-not-printed'] });

        toast.success('Widget data refreshed');
    }, [queryClient, currentPage, pageSize]);

    /**
     * Get new exam info and refresh widget
     */
    const getNewExamInfo = useCallback(() => {
        toast.destroy('loading');
        toast.destroy("updating");

        setShowAddExamPane(false);

        // Invalidate queries to trigger automatic refetch
        queryClient.invalidateQueries({ queryKey: ['widgetData', 'result-letters-not-printed', currentPage, pageSize] });
        queryClient.invalidateQueries({ queryKey: ['widgetCount', 'result-letters-not-printed'] });
        
        toast.success('Widget data refreshed');
    }, [queryClient, currentPage, pageSize]);

    /**
     * Table columns configuration - memoized to prevent recreation
     */
    const columns = useMemo(() => [
        {
            title: 'Patient Name',
            dataIndex: 'patientName',
            key: 'patientName',
            width: 200,
        },
        {
            title: 'MRN',
            dataIndex: 'mrn',
            key: 'mrn',
            render: (mrn) => <span style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>{formatMRN(mrn)}</span>,
            width: 150,
        },
        {
            title: 'Facility',
            dataIndex: 'facilityName',
            key: 'facilityName',
            ellipsis: true,
            width: 150,
        },
        {
            title: 'Exam Date',
            dataIndex: 'examDateTime',
            key: 'examDateTime',
            render: (examDateTime) => formatDate(examDateTime),
            width: 120,
        },
        {
            title: 'Exam Type',
            dataIndex: 'examType',
            key: 'examType',
            ellipsis: true,
            width: 150,
        },
        {
            title: 'Result Date',
            dataIndex: 'resultDate',
            key: 'resultDate',
            render: (resultDate) => formatDate(resultDate),
            width: 120,
        },
        {
            title: 'BiRad',
            dataIndex: 'birad',
            key: 'birad',
            width: 80,
        },
        {
            title: 'Radiologist',
            dataIndex: 'radiologistName',
            key: 'radiologistName',
            ellipsis: true,
            width: 180,
        },
        {
            title: 'Actions',
            key: 'action',
            width: 140,
            fixed: 'right',
            render: (_, row, index) => {
                const rowId = `${row.patientResultId}-${index}`;
                const isPrinting = printingRowId === rowId;
                
                return (
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handlePrint(row, rowId);
                            }}
                            disabled={isPrinting}
                            className="px-3 py-1.5 rounded-lg font-medium text-sm transition-colors"
                            style={{
                                backgroundColor: isPrinting 
                                    ? (Globals.theme === 'dark' ? '#71717a' : '#d1d5db')
                                    : (Globals.theme === 'dark' ? '#ec4899' : '#db2777'),
                                color: 'white',
                                border: 'none',
                                cursor: isPrinting ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px',
                                opacity: isPrinting ? 0.7 : 1
                            }}
                            onMouseEnter={(e) => {
                                if (!isPrinting) {
                                    e.currentTarget.style.backgroundColor = 
                                        Globals.theme === 'dark' ? '#db2777' : '#be185d';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!isPrinting) {
                                    e.currentTarget.style.backgroundColor = 
                                        Globals.theme === 'dark' ? '#ec4899' : '#db2777';
                                }
                            }}
                        >
                            {isPrinting && (
                                <div 
                                    className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"
                                    style={{ borderTopColor: 'transparent' }}
                                />
                            )}
                            Print Result
                        </button>
                    </div>
                );
            }
        }
    ], [formatMRN, formatDate, handlePrint, printingRowId]);

    /**
     * Get count color based on theme
     */
    const getCountColor = () => {
        return Globals.theme === 'dark' ? '#ec4899' : '#db2777';
    };

    /**
     * Get count message
     */
    const getCountMessage = () => {
        return count === 1 ? 'result letter not printed' : 'result letters not printed';
    };

    // COLLAPSED STATE - Show only count
    if (isCollapsed && !isExpanded) {
        if (isLoadingCount) {
            return (
                <div className="flex items-center justify-center py-4">
                    <div 
                        className="animate-spin rounded-full h-8 w-8 border-b-2"
                        style={{ borderColor: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a' }}
                    />
                </div>
            );
        }

        return (
            <div className="flex flex-col items-center justify-center py-4">
                <div 
                    className="text-5xl font-light mb-2"
                    style={{ color: getCountColor() }}
                >
                    {count.toLocaleString()}
                </div>
                <div 
                    className="text-sm"
                    style={{ color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a' }}
                >
                    {getCountMessage()}
                </div>
            </div>
        );
    }

    // Success - no result letters not printed
    if (!isLoading && count === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-4">
                <CheckCircle 
                    className="h-16 w-16 mx-auto mb-4"
                    style={{ color: getCountColor() }}
                />
                <p 
                    style={{ color: getCountColor() }}
                >
                    No Result Letters Not Printed Found
                </p>
            </div>
        );
    }

    // EXPANDED STATE - Show table with server-side pagination
    return (
        <>
            <div className="space-y-4">
                {/* Ant Design Table with server-side pagination */}
                <Table
                    columns={columns}
                    dataSource={sortedResults}
                    rowKey={(record, index) => `${record.patientResultId}-${record.patientExamId}-${index}`}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: totalCount,
                        onChange: (page, newPageSize) => {
                            setCurrentPage(page);
                            if (newPageSize !== pageSize) {
                                setPageSize(newPageSize);
                                setCurrentPage(1); // Reset to page 1 when page size changes
                            }
                        },
                        pageSizeOptions: ['25', '50', '100', '200'],
                        showSizeChanger: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} result letters`,
                    }}
                    size="small"
                    scroll={{ y: 500 }}
                    virtual
                    loading={(isLoading && !isExpanded) || isFetching}
                />
            </div>

            {/* Patient Exam Pane */}
            {showAddExamPane && selectedPatient && (
                <PatientExamPane
                    patientAge={calculateAge(selectedPatient?.dob)}
                    patientInactive={false}
                    patient={selectedPatient}
                    showPane={showAddExamPane}
                    examInfo={selectedPatientExam}
                    examHistory={examResults}
                    facility={facility}
                    updateExamInfo={updateExamInfo}
                    from='result-letters-not-printed'
                    hidePane={() => setShowAddExamPane(false)}
                    sharedFollowUpIDs={sharedFollowUpIDs}
                    getNewExamInfo={getNewExamInfo}
                />
            )}

            {/* Deletion Modal */}
            <Modal
                title="Reason For Deletion"
                visible={showDeletionModal}
                onCancel={closeDeletionModal}
                onOk={handleConfirmDelete}
                okText="Delete"
                cancelText="Cancel"
            >
                <div className="row">
                    {examToDelete && (
                        <div className="form-group col-12 mb-3">
                            <div style={{ 
                                padding: '12px', 
                                backgroundColor: Globals.theme === 'dark' ? '#3f3f46' : '#fef2f2', 
                                borderRadius: '6px',
                                border: `1px solid ${Globals.theme === 'dark' ? '#71717a' : '#fecaca'}`
                            }}>
                                <strong>Patient:</strong> {examToDelete.patientName}<br />
                                <strong>MRN:</strong> {examToDelete.mrn}<br />
                                <strong>Exam Type:</strong> {examToDelete.examType}<br />
                                <strong>Exam Date:</strong> {formatDate(examToDelete.examDateTime)}<br />
                                <strong>Result Date:</strong> {formatDate(examToDelete.resultDate)}<br />
                                <strong>BiRad:</strong> {examToDelete.birad}<br />
                                <strong>Radiologist:</strong> {examToDelete.radiologistName}
                            </div>
                        </div>
                    )}
                    
                    {examToDelete?.groupedExam && (
                        <div className="form-group col-12 mb-3">
                            <p>Would you also like to delete the grouped exam?: <strong><u>{groupedExamType || examToDelete.groupedExam}</u></strong>&nbsp;<span style={{ color: '#ec4899' }}>*</span></p>
                            <div style={{ marginLeft: '12px' }}>
                                <div className="custom-control custom-radio custom-control-inline">
                                    <input 
                                        type="radio" 
                                        name="deleteGroupedExam" 
                                        className="custom-control-input" 
                                        value="Yes" 
                                        checked={shouldDeleteGroupedExam === true} 
                                        onChange={() => setShouldDeleteGroupedExam(true)} 
                                    />
                                    <span className="custom-control-label">Yes</span>
                                </div>
                                <div className="custom-control custom-radio custom-control-inline">
                                    <input 
                                        type="radio" 
                                        name="deleteGroupedExam" 
                                        className="custom-control-input" 
                                        value="No" 
                                        checked={shouldDeleteGroupedExam === false} 
                                        onChange={() => setShouldDeleteGroupedExam(false)} 
                                    />
                                    <span className="custom-control-label">No</span>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    <div className="form-group col-12">
                        <label className="form-label">
                            Reason for Deletion <span style={{ color: '#ec4899' }}>*</span>
                        </label>
                        <select 
                            className="form-control-custom" 
                            name="deletionReason" 
                            value={deletionReason} 
                            onChange={(e) => setDeletionReason(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '8px',
                                borderRadius: '6px',
                                border: `1px solid ${Globals.theme === 'dark' ? '#3f3f46' : '#e5e7eb'}`,
                                backgroundColor: Globals.theme === 'dark' ? '#18181b' : 'white',
                                color: Globals.theme === 'dark' ? '#e4e4e7' : '#18181b'
                            }}
                        >
                            <option value="">-- Select a reason --</option>
                            {deletionReasons.map((item, idx) => (
                                <option key={idx} value={item.reasonForDeletionID}>
                                    {item.reasonForDeletion}
                                </option>
                            ))}
                        </select>
                    </div>
                    
                    <div className="form-group col-12">
                        <em style={{ color: '#dc2626' }}>
                            <strong>NOTE:&nbsp;</strong>
                            Deleting this exam will permanently remove it from the system
                        </em>
                    </div>
                </div>
            </Modal>
        </>
    );
}
